const userId = row => row?.user?.id

export const dedupNearest = rows => {
  const byUser = new Map()
  rows.forEach(r => byUser.set(userId(r), r))
  return [...byUser.values()]
}

const childNodes = node => [...(node?.estates || []), ...(node?.buildings || [])]

export const collectDescendantUserIds = node => {
  const ids = new Set()
  const visit = n => {
    (n.members || []).forEach(m => ids.add(userId(m)))
    childNodes(n).forEach(visit)
  }
  childNodes(node).forEach(visit)
  return ids
}

export const nodeAccessCounts = (node, inherited = []) => {
  const directIds = new Set((node?.members || []).map(userId))
  const inheritedHere = dedupNearest(inherited.filter(i => !directIds.has(userId(i))))
  const viaRows = node?.type === 'building'
    ? (node.cascadedMembers || []).filter(c => !directIds.has(userId(c)))
    : inheritedHere
  const inheritedIds = new Set(viaRows.map(userId))
  const descendantIds = collectDescendantUserIds(node)
  const insideIds = [...descendantIds].filter(id => !directIds.has(id) && !inheritedIds.has(id))
  return {
    directCount: directIds.size,
    inheritedCount: viaRows.length,
    insideCount: insideIds.length,
    viaRows,
    inheritedHere,
    directIds,
    inheritedIds,
    insideIds
  }
}

const collectNodeKeys = node => {
  const keys = new Set()
  const visit = n => {
    keys.add(`${n.type}:${n.id}`)
    childNodes(n).forEach(visit)
  }
  if (node) visit(node)
  return keys
}

const collectCascaded = node => {
  const rows = []
  const visit = n => {
    (n.cascadedMembers || []).forEach(r => rows.push(r))
    childNodes(n).forEach(visit)
  }
  if (node) visit(node)
  return rows
}

export const treeAccessSummary = tree => {
  if (!tree) return null
  const root = nodeAccessCounts(tree, [])
  const insideIds = new Set(root.insideIds)
  const keys = collectNodeKeys(tree)
  const above = new Map()
  const viaRules = new Set()
  const cascaded = collectCascaded(tree)
  cascaded.forEach(row => {
    const id = userId(row)
    const sourceType = row.sourceType || row.source_type
    const sourceId = row.sourceId ?? row.source_id
    if (keys.has(`${sourceType}:${sourceId}`)) return
    if (root.directIds.has(id) || insideIds.has(id)) return
    if (!above.has(id)) above.set(id, { sourceType, sourceName: row.sourceName || row.source_name || null })
  })
  cascaded.forEach(row => {
    const id = userId(row)
    if (id === undefined || root.directIds.has(id) || insideIds.has(id) || above.has(id)) return
    viaRules.add(id)
  })
  const aboveRows = [...above.values()]
  const aboveNames = [...new Set(aboveRows.map(r => r.sourceName).filter(Boolean))]
  const aboveTypes = [...new Set(aboveRows.map(r => r.sourceType).filter(Boolean))]
  return {
    direct: root.directCount,
    inside: root.insideCount,
    above: aboveRows.length,
    aboveName: aboveNames.length === 1 ? aboveNames[0] : null,
    aboveType: aboveTypes.length === 1 ? aboveTypes[0] : null,
    domain: viaRules.size,
    total: root.directCount + root.insideCount + aboveRows.length + viaRules.size
  }
}

const people = n => `${n.toLocaleString('en-GB')} ${n === 1 ? 'person' : 'people'}`

export const treeAccessSentence = (summary, scopeType) => {
  if (!summary || summary.total === 0) return null
  const parts = []
  if (summary.direct > 0) parts.push(`${people(summary.direct)} added to this ${scopeType} directly`)
  if (summary.inside > 0) parts.push(`${people(summary.inside)} added to buildings${scopeType === 'portfolio' ? ' or estates' : ''} inside it`)
  if (summary.above > 0) {
    const from = summary.aboveName
      ? `from ${summary.aboveName} above`
      : `from the ${summary.aboveType || 'level'} above`
    parts.push(`${people(summary.above)} ${from}`)
  }
  let list = parts.join(', ')
  if (summary.domain > 0) {
    const rule = `${summary.domain.toLocaleString('en-GB')} ${parts.length ? 'more ' : summary.domain === 1 ? 'person ' : 'people '}through domain rules`
    list = parts.length ? `${list}, and ${rule}` : rule
  }
  return `Where access comes from: ${list}. Anyone added in more than one place is counted once, at the level closest to this ${scopeType}.`
}
